import ClearIcon from '@mui/icons-material/Clear';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import SearchIcon from '@mui/icons-material/Search';
import ShieldOutlined from '@mui/icons-material/ShieldOutlined';
import {
  Box,
  Chip,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useContext, useEffect, useMemo, useState } from 'react';
import { AiFillCalendar } from 'react-icons/ai';
import { useSearchParams } from 'react-router-dom';

import BuscadorClubTab from '../components/buscadores/BuscadorClubTab';
import BuscadorJugadorTab from '../components/buscadores/BuscadorJugadorTab';
import { ContainerApp, LoadingApp } from '../components/custom/CustomComponents';
import TodasLigas from '../components/ligas/TodasLigas';
import { Context } from '../service/Context';
import { capitalizar, CATEGORIAS_ORDEN } from '../utils/UtilesComun';

/**
 * Pantalla principal de exploración de competiciones y ligas.
 */
const ExplorarLigas = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [tabActiva, setTabActiva] = useState(() => {
    if (tabParam === 'clubes') {
      return 1;
    }
    if (tabParam === 'jugadores') {
      return 2;
    }
    return 0;
  });

  const { obtenerTodasLigas, obtenerTemporadas, temporada, modificarTemporada, temporadasCache, ligasExplorarCache } =
    useContext(Context);

  const temporadaInicial = temporada || (temporadasCache && temporadasCache.length > 0 ? temporadasCache[0] : '');
  const ligasIniciales = (temporadaInicial && ligasExplorarCache?.[temporadaInicial]) || [];

  const [ligas, setLigas] = useState(ligasIniciales);
  const [temporadas, setTemporadas] = useState(temporadasCache || []);
  const [temporadaSeleccionada, setTemporadaSeleccionada] = useState(temporadaInicial);
  const [loading, setLoading] = useState(ligasIniciales.length === 0);

  // Estados de filtrado en cliente
  const [filtroTexto, setFiltroTexto] = useState('');
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState('TODAS');

  useEffect(() => {
    if (tabParam === 'clubes') {
      setTabActiva(1);
    } else if (tabParam === 'jugadores') {
      setTabActiva(2);
    } else if (tabParam === 'ligas') {
      setTabActiva(0);
    }
  }, [tabParam]);

  const handleTabChange = (nuevoValor) => {
    setTabActiva(nuevoValor);
    const tabNames = ['ligas', 'clubes', 'jugadores'];
    setSearchParams({ tab: tabNames[nuevoValor] });
  };

  // Cargar temporadas disponibles
  useEffect(() => {
    async function cargarTemporadas() {
      const force = Boolean(temporadasCache && temporadasCache.length > 0);
      const temporadasObtenidas = await obtenerTemporadas(force);
      if (temporadasObtenidas && temporadasObtenidas.length > 0) {
        setTemporadas(temporadasObtenidas);

        if (temporada) {
          setTemporadaSeleccionada(temporada);
        } else if (!temporadaSeleccionada) {
          setTemporadaSeleccionada(temporadasObtenidas[0]);
          modificarTemporada(temporadasObtenidas[0]);
        }
      }
    }
    cargarTemporadas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cargar ligas cuando cambia la temporada seleccionada
  useEffect(() => {
    async function cargarDatos() {
      if (temporadaSeleccionada) {
        const cachedLigas = ligasExplorarCache?.[temporadaSeleccionada];
        if (cachedLigas && cachedLigas.length > 0) {
          setLigas(cachedLigas);
          setLoading(false);
        } else {
          setLoading(true);
        }
        const force = Boolean(cachedLigas && cachedLigas.length > 0);
        const ligasObtenidas = await obtenerTodasLigas(temporadaSeleccionada, force);
        if (ligasObtenidas) {
          setLigas(ligasObtenidas);
        }
        setLoading(false);
      }
    }
    cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [temporadaSeleccionada]);

  const handleTemporadaChange = async (nuevaTemporada) => {
    if (nuevaTemporada && nuevaTemporada !== temporadaSeleccionada) {
      const cached = ligasExplorarCache?.[nuevaTemporada];
      if (cached && cached.length > 0) {
        setLigas(cached);
      } else {
        setLoading(true);
      }
      setTemporadaSeleccionada(nuevaTemporada);
      await modificarTemporada(nuevaTemporada);
    }
  };

  // Conteo de ligas por categoría para los chips de filtro rápido
  const resumenCategorias = useMemo(() => {
    const counts = {};
    ligas.forEach((l) => {
      const cat = l.categoria?.toUpperCase() || 'OTROS';
      counts[cat] = (counts[cat] || 0) + 1;
    });

    const ordenadas = CATEGORIAS_ORDEN.filter((cat) => counts[cat]);
    Object.keys(counts).forEach((cat) => {
      if (!CATEGORIAS_ORDEN.includes(cat)) {
        ordenadas.push(cat);
      }
    });

    return ordenadas.map((cat) => ({
      categoria: cat,
      nombre: capitalizar(cat),
      count: counts[cat],
    }));
  }, [ligas]);

  if (loading && !temporadaSeleccionada && temporadas.length === 0 && tabActiva === 0) {
    return <LoadingApp />;
  }

  return (
    <ContainerApp maxWidth={false}>
      {/* Selector de Pestañas Superior: Ligas, Clubes, Jugadores */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
        <Tabs
          value={tabActiva}
          onChange={(e, val) => handleTabChange(val)}
          textColor="primary"
          indicatorColor="primary"
          sx={{
            minHeight: 40,
            backgroundColor: '#ffffff',
            p: 0.5,
            borderRadius: '10px',
            border: '1px solid #cbd5e1',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.88rem',
              minHeight: 36,
              py: 0.75,
              px: 3,
              borderRadius: '8px',
              color: '#64748b',
              transition: 'all 0.15s ease',
              '&.Mui-selected': {
                color: '#0f766e',
                fontWeight: 700,
              },
              '&:hover': {
                color: '#0f766e',
                backgroundColor: '#f8fafc',
              },
            },
          }}
        >
          <Tab icon={<EmojiEventsIcon sx={{ fontSize: '1.2rem', mr: 0.5 }} />} iconPosition="start" label="Ligas" />
          <Tab icon={<ShieldOutlined sx={{ fontSize: '1.2rem', mr: 0.5 }} />} iconPosition="start" label="Clubes" />
          <Tab
            icon={<PersonSearchIcon sx={{ fontSize: '1.25rem', mr: 0.5 }} />}
            iconPosition="start"
            label="Jugadores"
          />
        </Tabs>
      </Box>

      {/* Pestaña 0: Ligas */}
      {tabActiva === 0 && (
        <>
          {/* Cabecera principal de la pantalla */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
              mb: 2.5,
            }}
          >
            {/* Título y metadatos */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: '10px',
                  backgroundColor: 'rgba(15, 118, 110, 0.08)',
                  color: '#0f766e',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid rgba(15, 118, 110, 0.20)',
                }}
              >
                <EmojiEventsIcon sx={{ fontSize: 24 }} />
              </Box>

              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '1.25rem', sm: '1.45rem' },
                    color: 'text.primary',
                    lineHeight: 1.2,
                  }}
                >
                  Explorar Ligas
                </Typography>
              </Box>
            </Box>

            {/* Selector de temporada integrado */}
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                height: 36,
                px: 1.25,
                backgroundColor: '#ffffff',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
                transition: 'all 0.15s ease-in-out',
                '&:hover': { borderColor: '#0f766e' },
              }}
            >
              <AiFillCalendar style={{ color: '#0f766e', fontSize: '1.1rem' }} />
              <Select
                id="temporada-select"
                name="temporada"
                value={temporadaSeleccionada || ''}
                onChange={(e) => handleTemporadaChange(e.target.value)}
                variant="standard"
                disableUnderline
                displayEmpty
                sx={{
                  fontSize: '0.82rem',
                  fontWeight: 700,
                  color: '#1e293b',
                  '& .MuiSelect-select': { py: 0, pl: 0.5, pr: '18px !important' },
                  '& .MuiSelect-icon': { color: '#64748b', fontSize: '1rem', right: 0 },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                      '& .MuiMenuItem-root': {
                        fontSize: '0.82rem',
                        py: 0.75,
                      },
                    },
                  },
                }}
              >
                {temporadas.map((temp) => (
                  <MenuItem key={temp} value={temp}>
                    {temp}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Box>

          {/* Barra de herramientas: Buscador y Filtro por categorías */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'stretch', md: 'center' },
              justifyContent: 'space-between',
              gap: 1.5,
              p: 1.25,
              mb: 2.5,
              backgroundColor: '#f8fafc',
              borderRadius: '10px',
              border: '1px solid #e2e8f0',
            }}
          >
            {/* Pestañas de categoría (chips de navegación rápida) */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                overflowX: 'auto',
                py: 0.25,
                '&::-webkit-scrollbar': { height: 4 },
                '&::-webkit-scrollbar-thumb': { backgroundColor: '#cbd5e1', borderRadius: 4 },
              }}
            >
              <Chip
                label={`Todas (${ligas.length})`}
                onClick={() => setCategoriaSeleccionada('TODAS')}
                size="small"
                sx={{
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  height: 30,
                  px: 0.5,
                  borderRadius: '6px',
                  backgroundColor: categoriaSeleccionada === 'TODAS' ? '#0f766e' : '#ffffff',
                  color: categoriaSeleccionada === 'TODAS' ? '#ffffff' : '#475569',
                  border: '1px solid',
                  borderColor: categoriaSeleccionada === 'TODAS' ? '#0f766e' : '#cbd5e1',
                  '&:hover': {
                    backgroundColor: categoriaSeleccionada === 'TODAS' ? '#0d665f' : '#f1f5f9',
                  },
                }}
              />

              {resumenCategorias.map((item) => {
                const activa = categoriaSeleccionada === item.categoria;
                return (
                  <Chip
                    key={item.categoria}
                    label={`${item.nombre} (${item.count})`}
                    onClick={() => setCategoriaSeleccionada(item.categoria)}
                    size="small"
                    sx={{
                      cursor: 'pointer',
                      fontWeight: activa ? 700 : 600,
                      fontSize: '0.75rem',
                      height: 30,
                      px: 0.5,
                      borderRadius: '6px',
                      backgroundColor: activa ? '#0f766e' : '#ffffff',
                      color: activa ? '#ffffff' : '#475569',
                      border: '1px solid',
                      borderColor: activa ? '#0f766e' : '#cbd5e1',
                      '&:hover': {
                        backgroundColor: activa ? '#0d665f' : '#f1f5f9',
                      },
                    }}
                  />
                );
              })}
            </Box>

            {/* Campo de búsqueda rápida */}
            <TextField
              size="small"
              value={filtroTexto}
              onChange={(e) => setFiltroTexto(e.target.value)}
              placeholder=""
              sx={{
                minWidth: { xs: '100%', md: 260 },
                '& .MuiOutlinedInput-root': {
                  height: 32,
                  fontSize: '0.80rem',
                  backgroundColor: '#ffffff',
                  borderRadius: '6px',
                  '& fieldset': { borderColor: '#cbd5e1' },
                  '&:hover fieldset': { borderColor: '#0f766e' },
                  '&.Mui-focused fieldset': { borderColor: '#0f766e' },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: '1.05rem', color: '#64748b' }} />
                  </InputAdornment>
                ),
                endAdornment: filtroTexto ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setFiltroTexto('')} sx={{ p: 0.25 }}>
                      <ClearIcon sx={{ fontSize: '0.90rem', color: '#94a3b8' }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Box>

          {/* Listado de ligas organizado */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <LoadingApp />
            </Box>
          ) : (
            <TodasLigas ligas={ligas} filtroTexto={filtroTexto} categoriaSeleccionada={categoriaSeleccionada} />
          )}
        </>
      )}

      {/* Pestaña 1: Clubes */}
      {tabActiva === 1 && <BuscadorClubTab />}

      {/* Pestaña 2: Jugadores */}
      {tabActiva === 2 && <BuscadorJugadorTab />}
    </ContainerApp>
  );
};

export default ExplorarLigas;
